import { DownloadOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { Alert, Button, DatePicker, Form, Input, InputNumber, Modal, Select, Space, Table, message } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { downloadFile, errorMessage } from '../../api/client';
import { fetchDealerReturnImageUrl, useAddDealerReturn, useDealerPlacements, useDealerReturns } from '../../api/hooks';
import { useAuth } from '../../context/AuthContext';
import type { DealerPrincipalReturn } from '../../types';
import { apiDate, date, money } from '../../utils/format';
import { maxAmountRule } from '../../utils/maxAmountRule';
import { paymentMethodOptions } from '../../utils/paymentMethods';
import { trancheOptionLabel, withdrawalShare } from '../../utils/withdrawalShare';
import PendingDocumentPicker from '../investor/PendingDocumentPicker';
import TransactionImageLink from '../investor/TransactionImageLink';
import TransactionAccountField from '../common/TransactionAccountField';
import { useTransactionAccountField } from '../../utils/transactionAccountField';
import QuickShareButton from '../common/QuickShareButton';
import { usePartyContact } from '../common/usePartyContact';
import { transactionMessage } from '../../utils/quickShare';
import { useTranslation } from 'react-i18next';
import AutoText from '../common/AutoText';

/** Mirrors the investor Withdrawals tab: money coming back out of a specific tranche. */
export default function DealerWithdrawalsTab({ dealerId }: { dealerId: number }) {
  const { t } = useTranslation();
  const { hasRole } = useAuth();
  const canWrite = hasRole('Admin');
  const { data, isLoading } = useDealerReturns(dealerId);
  const { data: placements } = useDealerPlacements(dealerId);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const [transactionImage, setTransactionImage] = useState<File | null>(null);
  const addReturn = useAddDealerReturn(dealerId);
  const accountField = useTransactionAccountField('Dealer', dealerId);
  const contact = usePartyContact('Dealer', dealerId);

  // Watched so the share-of-investment line can update as the amount is typed.
  const selectedPlacementId = Form.useWatch('placementId', form) as number | undefined;
  const enteredAmount = Form.useWatch('amount', form) as number | undefined;
  const paymentMethod = Form.useWatch('paymentMethod', form) as string | undefined;

  const openPlacements = (placements ?? []).filter((p) => p.remainingAmount > 0);

  // Total investment still out with this dealer, across every tranche — the denominator throughout.
  const totalRemaining = (placements ?? []).reduce((sum, p) => sum + p.remainingAmount, 0);

  // Only tranches with principal left can be drawn against. Each option shows its own profit rate,
  // so it is clear which tranche's earning power a withdrawal would be giving up.
  const options = openPlacements.map((p) => ({
    value: p.id,
    label: trancheOptionLabel(p.id, date(p.placementDate), p.remainingAmount, p.profitRate),
  }));

  const selected = openPlacements.find((p) => p.id === selectedPlacementId);

  const share = selected
    ? withdrawalShare(enteredAmount, selected.id, selected.remainingAmount, totalRemaining)
    : null;

  const onFinish = async (values: Record<string, unknown>) => {
    try {
      await addReturn.mutateAsync({
        placementId: values.placementId as number,
        amount: values.amount as number,
        returnDate: apiDate(values.returnDate as dayjs.Dayjs)!,
        reason: values.reason as string | undefined,
        paymentMethod: values.paymentMethod as string | undefined,
        transactionImage,
      });
      await accountField.commitIfChanged();
      message.success('Withdrawal recorded.');
      form.resetFields();
      setTransactionImage(null);
      setOpen(false);
    } catch (err) {
      message.error(errorMessage(err));
    }
  };

  return (
    <>
      <Space style={{ marginBottom: 16 }} wrap>
        {canWrite && (
          <Button
            danger
            type="primary"
            icon={<MinusCircleOutlined />}
            disabled={options.length === 0}
            onClick={() => {
              form.setFieldsValue({ returnDate: dayjs() });
              setOpen(true);
            }}
          >
            Withdraw from total investment
          </Button>
        )}
        <Button
          icon={<DownloadOutlined />}
          onClick={() =>
            downloadFile(
              `/reports/dealer-withdrawal-statement/${dealerId}`,
              `withdrawal_statement_${dealerId}.pdf`,
            ).catch((err) => message.error(errorMessage(err)))
          }
        >
          Download withdrawal statement (PDF)
        </Button>
      </Space>

      <Table<DealerPrincipalReturn>
        rowKey="id"
        loading={isLoading}
        dataSource={data ?? []}
        scroll={{ x: 'max-content' }}
        columns={[
          { title: '#', dataIndex: 'id', width: 70 },
          { title: t('common.date'), dataIndex: 'returnDate', render: date },
          { title: t('common.amount'), dataIndex: 'amount', align: 'right', render: money },
          { title: 'Remaining investment', dataIndex: 'remainingAmount', align: 'right', render: money },
          { title: t('finance.paymentMethod'), dataIndex: 'paymentMethod', render: (v: string | null) => v ?? '—' },
          { title: t('party.reason'), dataIndex: 'reason', render: (v: string | null) => <AutoText value={v} /> },
          {
            title: t('finance.transactionProof'),
            render: (_, row) => (
              <TransactionImageLink
                exists={row.hasTransactionImage}
                title={`Transaction proof — withdrawal #${row.id}`}
                fetchUrl={() => fetchDealerReturnImageUrl(dealerId, row.id)}
              />
            ),
          },
          {
            title: t('finance.receipt'),
            render: (_, row) => (
              <Button
                type="link"
                size="small"
                icon={<DownloadOutlined />}
                onClick={() =>
                  downloadFile(`/receipts/dealer-withdrawal/${row.id}`, `withdrawal_receipt_${row.id}.pdf`).catch(
                    (err) => message.error(errorMessage(err)),
                  )
                }
              >
                PDF
              </Button>
            ),
          },
          ...(contact.ready
            ? [
                {
                  title: t('common.share'),
                  render: (_: unknown, row: DealerPrincipalReturn) => (
                    <QuickShareButton
                      phone={contact.phone}
                      recipientName={contact.name}
                      message={transactionMessage(contact, 'Capital withdrawal', row.amount, row.returnDate)}
                    />
                  ),
                },
              ]
            : []),
        ]}
      />

      <Modal maskClosable={false}
        title="Withdraw from total investment"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={addReturn.isPending}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onFinish} preserve={false}>
          <Form.Item name="placementId" label="Investment tranche" rules={[{ required: true }]}>
            <Select options={options} placeholder="Select a tranche" />
          </Form.Item>
          <Form.Item
            name="amount"
            label={t('finance.amountPkr')}
            rules={[
              { required: true },
              { type: 'number', min: 0.01, message: 'Amount must be greater than zero.' },
              maxAmountRule(selected?.remainingAmount, 'remaining investment in this tranche'),
            ]}
          >
            <InputNumber min={0.01} style={{ width: '100%' }} />
          </Form.Item>

          {share && (
            <Alert
              type={share.overdrawn ? 'error' : 'info'}
              showIcon
              style={{ marginBottom: 16, marginTop: -8 }}
              message={share.text}
            />
          )}

          <Form.Item name="returnDate" label={t('finance.withdrawalDate')} rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} disabledDate={(d) => d.isAfter(dayjs(), 'day')} />
          </Form.Item>
          <Form.Item name="paymentMethod" label={t('finance.paymentMethod')}>
            <Select placeholder="How the money came back" allowClear options={paymentMethodOptions} />
          </Form.Item>
          <TransactionAccountField field={accountField} paymentMethod={paymentMethod} />
          <Form.Item name="reason" label={t('party.reason')}>
            <Input.TextArea rows={2} />
          </Form.Item>

          <PendingDocumentPicker
            type="withdrawal-image"
            label={t('finance.transactionProofOptional')}
            hint="Receipt or transfer screenshot proving the amount was returned · JPG, PNG or WebP · max 5 MB"
            file={transactionImage}
            onChange={setTransactionImage}
          />
        </Form>
      </Modal>
    </>
  );
}
